import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Calendar, CalendarClock, MapPin, ChevronRight, LogOut, Plane, Briefcase,
  Loader2, History, Sun, ArrowRight, Settings, X, KeyRound,
  Home, User, Phone, Mail, Receipt, Camera, Check,
} from 'lucide-react';
import { driverApi, driverLogout } from '../lib/driverAuth';

const OPERATOR_TEL = '03333357733';
const OPERATOR_DISPLAY = '0333 335 7733';
const OFFICE_EMAIL = 'info@obsidianchauffeurs.co.uk';
const RECEIPT_CATEGORIES = ['Parking', 'Tolls', 'Fuel'];

const TAB_DEFS = [
  { key: 'today',    label: 'Today',    icon: Sun },
  { key: 'tomorrow', label: 'Tomorrow', icon: CalendarClock },
  { key: 'upcoming', label: 'Upcoming', icon: Calendar },
];

const initials = (name = '') =>
  name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?';

const ukShortDate = (s) => {
  if (!s) return '';
  try {
    return new Date(s + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'short', day: '2-digit', month: 'short',
    });
  } catch { return s; }
};

const StatusPill = ({ status, driverStatus }) => {
  let label = '—', cls = 'bg-neutral-100 text-neutral-700';
  if (status === 'cancelled')                { label = 'Cancelled'; cls = 'bg-red-100 text-red-800'; }
  else if (driverStatus === 'completed' || status === 'completed') {
    label = 'Completed'; cls = 'bg-neutral-200 text-neutral-700';
  }
  else if (driverStatus === 'pob')           { label = 'POB';       cls = 'bg-emerald-100 text-emerald-800'; }
  else if (driverStatus === 'arrived')       { label = 'Arrived';   cls = 'bg-blue-100 text-blue-800'; }
  else if (driverStatus === 'on_the_way')    { label = 'On The Way'; cls = 'bg-purple-100 text-purple-800'; }
  else if (status === 'confirmed')           { label = 'Confirmed'; cls = 'bg-green-100 text-green-800'; }
  else                                       { label = status || 'Pending'; }
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

const JobCard = ({ job, onOpen }) => {
  const name = [job.firstName, job.lastName].filter(Boolean).join(' ').trim() || 'Passenger';
  return (
    <button
      type="button"
      onClick={() => onOpen(job.id)}
      className="w-full text-left bg-white border border-neutral-200 rounded-2xl p-4 hover:border-black active:bg-neutral-50 transition-colors"
      data-testid={`job-card-${job.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-2xl font-bold text-black leading-none">{job.pickupTime || '—'}</p>
          <p className="text-xs text-neutral-500 mt-1">{ukShortDate(job.pickupDate)}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusPill status={job.status} driverStatus={job.driverStatus} />
          {job.flightNumber && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono text-neutral-500 uppercase">
              <Plane className="h-3 w-3" /> {String(job.flightNumber).toUpperCase()}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-start gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-500 mt-2 shrink-0" />
          <p className="text-sm text-neutral-800 truncate flex-1">{job.pickupLocation || '—'}</p>
        </div>
        <div className="flex items-start gap-2">
          <span className="h-2 w-2 rounded-sm bg-red-600 mt-2 shrink-0" />
          <p className="text-sm text-neutral-600 truncate flex-1">{job.dropoffLocation || '—'}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-neutral-100">
        <p className="text-xs font-semibold text-neutral-700 truncate">{name}</p>
        <ChevronRight className="h-4 w-4 text-neutral-400" />
      </div>
    </button>
  );
};

const SettingsSheet = ({ open, onClose, driver }) => {
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCurrentPin(''); setNewPin(''); setConfirmPin(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (newPin.length < 4 || newPin.length > 6) { toast.error('New PIN must be 4-6 digits'); return; }
    if (newPin !== confirmPin) { toast.error('PINs do not match'); return; }
    setSubmitting(true);
    try {
      await driverApi.post('/driver/auth/change-pin', { currentPin, newPin });
      toast.success('PIN updated');
      reset();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update PIN');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="dj-settings-sheet">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 pb-8 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Account</p>
            <p className="text-xl font-semibold text-black">Settings</p>
            <p className="text-xs text-neutral-500 mt-0.5 truncate">{driver?.name} · {driver?.phone}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" aria-label="Close" data-testid="dj-settings-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="dj-change-pin-form">
          <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5" /> Change PIN
          </p>
          {[
            { label: 'Current PIN', val: currentPin, setVal: setCurrentPin, key: 'current', testid: 'dj-pin-current' },
            { label: 'New PIN (4-6 digits)', val: newPin, setVal: setNewPin, key: 'new', testid: 'dj-pin-new' },
            { label: 'Confirm new PIN', val: confirmPin, setVal: setConfirmPin, key: 'confirm', testid: 'dj-pin-confirm' },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-xs text-neutral-600 mb-1">{f.label}</label>
              <input
                type="password" inputMode="numeric" maxLength={6}
                value={f.val}
                onChange={(e) => f.setVal(e.target.value.replace(/\D/g, ''))}
                className="w-full h-11 px-4 rounded-md border border-neutral-300 bg-white text-black text-lg tracking-[0.4em] text-center font-mono"
                placeholder="••••" data-testid={f.testid}
              />
            </div>
          ))}
          <button type="submit" disabled={submitting || !currentPin || !newPin}
            className="w-full h-12 rounded-md bg-black text-white font-semibold hover:bg-neutral-900 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="dj-pin-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? 'Updating…' : 'Update PIN'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ReceiptSheet = ({ open, onClose }) => {
  const [category, setCategory] = useState('Parking');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCategory('Parking'); setAmount(''); setNote(''); setFile(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { toast.error('Please enter the amount'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('category', category);
      fd.append('amount', amount);
      fd.append('note', note);
      if (file) fd.append('file', file);
      await driverApi.post('/driver/receipts', fd);
      toast.success('Receipt submitted');
      reset();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit receipt');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="dj-receipt-sheet">
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 pb-8 shadow-2xl max-h-[90vh] overflow-auto">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-500">Expenses</p>
            <p className="text-xl font-semibold text-black">Submit a Receipt</p>
            <p className="text-xs text-neutral-500 mt-0.5">Log parking, tolls or fuel paid on a job.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" aria-label="Close" data-testid="dj-receipt-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="dj-receipt-form">
          <div>
            <label className="block text-xs text-neutral-600 mb-1.5">Category</label>
            <div className="grid grid-cols-3 gap-2">
              {RECEIPT_CATEGORIES.map((c) => (
                <button
                  key={c} type="button" onClick={() => setCategory(c)}
                  className={`h-11 rounded-xl text-sm font-semibold border transition-colors ${
                    category === c ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500'
                  }`}
                  data-testid={`dj-receipt-cat-${c.toLowerCase()}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs text-neutral-600 mb-1">Amount (£)</label>
            <input
              type="number" inputMode="decimal" step="0.01" min="0"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full h-12 px-4 rounded-md border border-neutral-300 bg-white text-black text-lg font-semibold"
              placeholder="0.00" data-testid="dj-receipt-amount"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-600 mb-1">Note (optional)</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full h-11 px-4 rounded-md border border-neutral-300 bg-white text-black"
              placeholder="e.g. M6 Toll, airport short-stay" data-testid="dj-receipt-note"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-600 mb-1.5">Photo of receipt (optional)</label>
            <label className="flex items-center gap-3 h-14 px-4 rounded-xl border border-dashed border-neutral-300 cursor-pointer hover:border-black" data-testid="dj-receipt-photo-label">
              <Camera className="h-5 w-5 text-neutral-500" />
              <span className="text-sm text-neutral-600 truncate">{file ? file.name : 'Take a photo or choose a file'}</span>
              <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="dj-receipt-photo" />
            </label>
          </div>

          <button type="submit" disabled={submitting}
            className="w-full h-12 rounded-md bg-black text-white font-semibold hover:bg-neutral-900 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="dj-receipt-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {submitting ? 'Submitting…' : 'Submit Receipt'}
          </button>
        </form>
      </div>
    </div>
  );
};

const ProfileRow = ({ icon: Icon, title, subtitle, onClick, href, testid, danger }) => {
  const inner = (
    <>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${danger ? 'bg-red-50 text-red-600' : 'bg-neutral-100 text-neutral-700'}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className={`text-sm font-semibold ${danger ? 'text-red-600' : 'text-black'}`}>{title}</p>
        {subtitle && <p className="text-xs text-neutral-500 truncate">{subtitle}</p>}
      </div>
      <ChevronRight className="h-4 w-4 text-neutral-400 shrink-0" />
    </>
  );
  const cls = 'w-full flex items-center gap-3 bg-white border border-neutral-200 rounded-2xl p-3.5 hover:border-black active:bg-neutral-50 transition-colors';
  if (href) return <a href={href} className={cls} data-testid={testid}>{inner}</a>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

const BottomNav = ({ view, setView }) => {
  const items = [
    { key: 'home', label: 'Home', icon: Home },
    { key: 'history', label: 'History', icon: History },
    { key: 'profile', label: 'Profile', icon: User },
  ];
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="dj-bottom-nav"
    >
      <div className="max-w-2xl mx-auto grid grid-cols-3">
        {items.map((it) => {
          const Icon = it.icon;
          const active = view === it.key;
          return (
            <button
              key={it.key} type="button" onClick={() => setView(it.key)}
              className={`flex flex-col items-center justify-center gap-0.5 py-2.5 ${active ? 'text-black' : 'text-neutral-400'}`}
              data-testid={`dj-nav-${it.key}`}
            >
              <Icon className={`h-5 w-5 ${active ? '' : 'opacity-80'}`} strokeWidth={active ? 2.4 : 2} />
              <span className="text-[11px] font-medium">{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

const DriverJobs = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('home');          // home | history | profile
  const [tab, setTab] = useState('today');            // today | tomorrow | upcoming
  const [showSettings, setShowSettings] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);

  const load = async () => {
    try {
      const { data: res } = await driverApi.get('/driver/jobs');
      setData(res);
      if (res.counts.today > 0) setTab('today');
      else if (res.counts.tomorrow > 0) setTab('tomorrow');
      else setTab('upcoming');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const jobs = useMemo(() => (data ? data[tab] || [] : []), [data, tab]);

  const handleLogout = async () => {
    await driverLogout();
    navigate('/driver/login', { replace: true });
  };
  const handleOpen = (id) => navigate(`/driver/${id}`);

  const driver = data?.driver;
  const topJob = data?.today?.[0] || data?.tomorrow?.[0] || data?.upcoming?.[0];
  const headerPad = { paddingTop: 'calc(env(safe-area-inset-top) + 1.25rem)' };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 pb-24" data-testid="driver-jobs">
      {view === 'home' && (
        <>
          {/* Header */}
          <div className="bg-black text-white px-5 sm:px-8 pb-8" style={headerPad}>
            <div className="max-w-2xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center text-sm font-bold">
                    {initials(driver?.name)}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.32em] text-white/50">Obsidian</p>
                    <p className="text-sm font-semibold tracking-wide">{driver?.name || 'Driver'}</p>
                  </div>
                </div>
              </div>

              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">Your jobs</p>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
                {data?.counts?.today || 0}<span className="text-white/60 italic font-light"> Today</span>
                <span className="text-white/30 mx-1.5">·</span>
                {data?.counts?.tomorrow || 0}<span className="text-white/60 italic font-light"> Tomorrow</span>
              </h1>
              {topJob && (
                <button
                  onClick={() => handleOpen(topJob.id)}
                  className="mt-5 w-full bg-white text-black rounded-2xl p-4 flex items-center gap-3 text-left hover:bg-neutral-100"
                  data-testid="dj-open-top"
                >
                  <div className="h-11 w-11 rounded-xl bg-black text-white flex items-center justify-center text-xs font-bold">NEXT</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{topJob.pickupTime} · {topJob.pickupLocation}</p>
                    <p className="text-xs text-neutral-500 truncate">→ {topJob.dropoffLocation}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0" />
                </button>
              )}
            </div>
          </div>

          {/* Today / Tomorrow / Upcoming tabs */}
          <div className="max-w-2xl mx-auto px-5 sm:px-8 -mt-4">
            <div className="bg-white rounded-2xl border border-neutral-200 p-1 grid grid-cols-3 gap-1 shadow-sm" data-testid="dj-tabs">
              {TAB_DEFS.map((t) => {
                const Icon = t.icon;
                const count = data?.counts?.[t.key] || 0;
                const active = tab === t.key;
                return (
                  <button
                    key={t.key} type="button" onClick={() => setTab(t.key)}
                    className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      active ? 'bg-black text-white' : 'text-neutral-600 hover:bg-neutral-50'
                    }`}
                    data-testid={`dj-tab-${t.key}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{t.label}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-white/15' : 'bg-neutral-100'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* List */}
          <div className="max-w-2xl mx-auto px-5 sm:px-8 mt-4 space-y-3">
            {jobs.length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-2xl p-10 text-center" data-testid="dj-empty">
                <Briefcase className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
                <p className="text-sm font-semibold text-black">No {tab} jobs</p>
                <p className="text-xs text-neutral-500 mt-1">
                  {tab === 'today' && 'Nothing scheduled for today. Enjoy the break.'}
                  {tab === 'tomorrow' && 'Nothing scheduled for tomorrow yet.'}
                  {tab === 'upcoming' && 'No future jobs assigned yet.'}
                </p>
              </div>
            ) : (
              jobs.map((j) => <JobCard key={j.id} job={j} onOpen={handleOpen} />)
            )}
          </div>
        </>
      )}

      {view === 'history' && (
        <>
          <div className="bg-black text-white px-5 sm:px-8 pb-7" style={headerPad}>
            <div className="max-w-2xl mx-auto">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-2">History</p>
              <h1 className="text-3xl font-bold tracking-tight">{data?.counts?.history || 0} past jobs</h1>
            </div>
          </div>
          <div className="max-w-2xl mx-auto px-5 sm:px-8 mt-4 space-y-3">
            {(data?.history || []).length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-2xl p-10 text-center" data-testid="dj-history-empty">
                <History className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
                <p className="text-sm font-semibold text-black">No history yet</p>
                <p className="text-xs text-neutral-500 mt-1">Completed jobs will appear here.</p>
              </div>
            ) : (
              (data?.history || []).map((j) => <JobCard key={j.id} job={j} onOpen={handleOpen} />)
            )}
          </div>
        </>
      )}

      {view === 'profile' && (
        <>
          {/* Profile header: name + number */}
          <div className="bg-black text-white px-5 sm:px-8 pb-8" style={headerPad}>
            <div className="max-w-2xl mx-auto">
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 mb-3">Profile</p>
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-full bg-white/10 text-white flex items-center justify-center text-lg font-bold">
                  {initials(driver?.name)}
                </div>
                <div className="min-w-0">
                  <p className="text-2xl font-bold tracking-tight truncate" data-testid="dj-profile-name">{driver?.name || 'Driver'}</p>
                  <p className="text-sm text-white/70" data-testid="dj-profile-phone">{driver?.phone || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto px-5 sm:px-8 mt-6 space-y-6">
            {/* Contact */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2 px-1">Contact</p>
              <div className="space-y-2.5">
                <ProfileRow icon={Phone} title="Call Operator" subtitle={OPERATOR_DISPLAY} href={`tel:${OPERATOR_TEL}`} testid="dj-call-operator" />
                <ProfileRow icon={Mail} title="Email Office" subtitle={OFFICE_EMAIL} href={`mailto:${OFFICE_EMAIL}`} testid="dj-email-office" />
              </div>
            </div>

            {/* Expenses */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2 px-1">Expenses</p>
              <ProfileRow icon={Receipt} title="Submit a Receipt" subtitle="Parking, tolls or fuel" onClick={() => setShowReceipt(true)} testid="dj-open-receipt" />
            </div>

            {/* Account */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2 px-1">Account</p>
              <div className="space-y-2.5">
                <ProfileRow icon={Settings} title="Settings" subtitle="Change your PIN" onClick={() => setShowSettings(true)} testid="dj-open-settings" />
                <ProfileRow icon={LogOut} title="Log out" onClick={handleLogout} testid="dj-logout" danger />
              </div>
            </div>
          </div>
        </>
      )}

      <BottomNav view={view} setView={setView} />
      <SettingsSheet open={showSettings} onClose={() => setShowSettings(false)} driver={driver} />
      <ReceiptSheet open={showReceipt} onClose={() => setShowReceipt(false)} />
    </div>
  );
};

export default DriverJobs;
